import React from "react";
import { Link } from "react-router-dom";
import { EXHIBITIONS } from "../data/site";
import { CalendarDays, CalendarPlus, ArrowUpRight } from "lucide-react";

function buildGoogleCalendarUrl({ title, start, end, allDay, details, location }) {
  const fmt = (d) => {
    if (allDay) return d.replace(/-/g, "");
    return d.replace(/[-:]/g, "").split(".")[0];
  };
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${fmt(start)}/${fmt(end)}`,
    details: details || "",
    location: location || "",
  });
  if (!allDay) params.set("ctz", "America/Denver");
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export default function UpcomingShows() {
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = EXHIBITIONS.upcoming.filter(
    (ex) => !ex.calendar?.end || ex.calendar.end.slice(0, 10) >= today
  );
  if (upcoming.length === 0) return null;
  const items = upcoming;
  if (items.length === 0) return null;

  return (
    <section
      className="border-t border-border"
      data-testid="upcoming-shows"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <header className="mb-12 md:mb-16 flex items-end justify-between gap-6">
          <div>
            <p className="label mb-3">Next — Upcoming</p>
            <h2 className="serif text-4xl md:text-5xl tracking-tight font-light">
              What's opening.
            </h2>
          </div>
          <p className="hidden md:block max-w-sm text-sm text-[hsl(var(--ink-soft))]">
            New exhibitions and openings. Subscribe at the bottom of the page for
            early invitations.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {items.map((ex) => (
            <article
              key={ex.id}
              className="border border-border bg-background overflow-hidden flex flex-col"
              data-testid={`upcoming-${ex.id}`}
            >
              <div className="aspect-square overflow-hidden bg-[hsl(var(--surface))] flex items-center justify-center p-4">
                <img
                  src={ex.image}
                  alt={ex.title}
                  className="max-w-full max-h-full w-auto h-auto object-contain"
                />
              </div>
              <div className="p-6 md:p-8 flex-1 flex flex-col">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  {ex.badge && (
                    <span className="inline-block bg-[hsl(var(--accent))] text-background px-3 py-1 text-[0.625rem] uppercase tracking-[0.18em]">
                      {ex.badge}
                    </span>
                  )}
                  <span className="label flex items-center gap-1">
                    <CalendarDays className="h-3.5 w-3.5" />
                    {ex.meta}
                  </span>
                </div>
                <h3 className="serif text-2xl md:text-3xl tracking-tight leading-tight">
                  {ex.title}
                </h3>
                <p className="mt-3 text-[hsl(var(--ink-soft))] leading-relaxed text-sm">
                  {ex.blurb}
                </p>
                <p className="mt-4 label">{ex.location}</p>
                <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
                {ex.featuring && (
                  <Link
                    to={`/work/${ex.featuring.slug}/${ex.featuring.id}`}
                    className="inline-flex items-center gap-2 self-start label link-underline text-foreground"
                    data-testid={`upcoming-featuring-${ex.id}`}
                  >
                    Featuring {ex.featuring.title} →
                  </Link>
                )}
                {ex.link && (
                  <a
                    href={ex.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 self-start label link-underline"
                    data-testid={`upcoming-link-${ex.id}`}
                  >
                    Venue website <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                )}
                {ex.calendar && (
                  <a
                    href={buildGoogleCalendarUrl({
                      title: `${ex.title} — Joseph Graves Jr.`,
                      start: ex.calendar.start,
                      end: ex.calendar.end,
                      allDay: ex.calendar.allDay,
                      details: `${ex.blurb}\n\nMore info: https://josephgravesjr.com`,
                      location: ex.location,
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 self-start label link-underline"
                    data-testid={`upcoming-cal-${ex.id}`}
                  >
                    <CalendarPlus className="h-3.5 w-3.5" />
                    Add to Calendar
                  </a>
                )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
